import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Trash2, Clock, ExternalLink } from 'lucide-react';
import { notificationsApi } from '../api';
import { playNotificationSound } from '../utils/notificationSound';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ru';

dayjs.extend(relativeTime);
dayjs.locale('ru');

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  entityType: string | null;
  entityId: string | null;
  createdAt: string;
}

export default function NotificationDropdown() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationsApi.list({ limit: 50 });
      const data = res.data;
      setNotifications(data.data);
      const newCount = data.unreadCount;
      if (prevCountRef.current > 0 && newCount > prevCountRef.current) {
        playNotificationSound();
      }
      prevCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30_000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkRead = async (id: string) => {
    try {
      await notificationsApi.markRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      // ignore
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // ignore
    }
  };

  const handleClearAll = async () => {
    try {
      await notificationsApi.clearAll();
      setNotifications([]);
      setUnreadCount(0);
    } catch {
      // ignore
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      handleMarkRead(notification.id);
    }
    if (notification.entityType && notification.entityId) {
      const routeMap: Record<string, string> = {
        task: '/tasks',
        lead: '/leads',
        deal: '/deals',
        company: '/companies',
        contact: '/contacts',
      };
      const route = routeMap[notification.entityType];
      if (route) {
        navigate(`${route}/${notification.entityId}`);
      }
    }
    setIsOpen(false);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'TASK_REMINDER': return '⏰';
      case 'TASK_ASSIGNED': return '✅';
      case 'LEAD_ASSIGNED': return '🎯';
      case 'DEAL_ASSIGNED': return '💰';
      case 'COMPANY_ASSIGNED': return '🏢';
      case 'CONTACT_ASSIGNED': return '👤';
      default: return '🔔';
    }
  };

  return (
    <div className="notification-dropdown-wrapper" ref={dropdownRef}>
      <button
        className="header-icon-btn"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <span className="notification-dropdown-title">Уведомления</span>
            <div className="notification-dropdown-actions">
              <button
                className="notification-action-btn"
                onClick={handleMarkAllRead}
                title="Прочитать все"
              >
                <CheckCheck size={16} />
              </button>
              <button
                className="notification-action-btn notification-action-danger"
                onClick={handleClearAll}
                title="Очистить все"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>

          <div className="notification-dropdown-body">
            {notifications.length === 0 ? (
              <div className="notification-empty">
                <Bell size={32} />
                <p>Нет уведомлений</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`notification-item ${!n.isRead ? 'notification-unread' : ''}`}
                  onClick={() => handleNotificationClick(n)}
                >
                  <div className="notification-item-icon">{getTypeIcon(n.type)}</div>
                  <div className="notification-item-content">
                    <div className="notification-item-title">{n.title}</div>
                    <div className="notification-item-message">{n.message}</div>
                    <div className="notification-item-meta">
                      <span className="notification-item-time">
                        <Clock size={12} />
                        {dayjs(n.createdAt).fromNow()}
                      </span>
                      {n.entityType && (
                        <span className="notification-item-link">
                          <ExternalLink size={12} />
                          Открыть
                        </span>
                      )}
                    </div>
                  </div>
                  {!n.isRead && (
                    <div
                      className="notification-item-dot"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkRead(n.id);
                      }}
                      title="Отметить прочитанным"
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
